import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { toast } from 'react-toastify'
import { ink, line, surface, radius, font, transition, cardStyle, bp as bpTokens } from 'features/dashboard/tokens'
import { RAG, scoreColour, scoreBand, scoreBandFor, formatPercent } from 'features/dashboard/charts/scoreColour'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import SearchableSelect from 'components/SearchableSelect'
import { fetchBuildingsAtRisk } from 'services/BpService'
import SharedBackLink from 'components/BackLink'

const PAGE = 25

const BuildingsAtRiskPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [buildings, setBuildings] = useState([])
  const [scored, setScored] = useState(0)
  const [visible, setVisible] = useState(PAGE)
  const [search, setSearch] = useState('')
  const [band, setBand] = useState('all')
  const [sort, setSort] = useState({ key: null, dir: 'asc' })

  const sortBy = key => setSort(s => s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: 'asc' })

  useEffect(() => {
    let active = true
    fetchBuildingsAtRisk()
      .then(res => {
        if (!active) return
        const data = res?.data?.data || {}
        setBuildings(data.buildings || [])
        setScored(data.scored || 0)
        setLoading(false)
      })
      .catch(() => {
        if (!active) return
        toast.error('Could not load buildings at risk.')
        setLoading(false)
      })
    return () => { active = false }
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let rows = buildings.filter(b => {
      if (band === 'overdue' && !(b.overdue > 0)) return false
      if (band !== 'all' && band !== 'overdue' && scoreBandFor(b.score)?.label !== band) return false
      if (!q) return true
      return [b.name, b.city, b.postcode, ...(b.reasons || [])]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    })
    if (sort.key) {
      const dir = sort.dir === 'asc' ? 1 : -1
      const val = b => sort.key === 'name' ? (b.name || '').toLowerCase()
        : sort.key === 'score' ? (b.score ?? -1)
        : sort.key === 'overdue' ? (b.overdue || 0) : 0
      rows = [...rows].sort((a, b) => {
        const av = val(a), bv = val(b)
        return av < bv ? -1 * dir : av > bv ? 1 * dir : 0
      })
    }
    return rows
  }, [buildings, search, band, sort])

  useEffect(() => { setVisible(PAGE) }, [search, band, sort])

  const shown = useMemo(() => filtered.slice(0, visible), [filtered, visible])

  return (
    <Page>
      <BackLink onClick={() => navigate('/dashboard')}>Back to dashboard</BackLink>

      <Header>
        <Eyebrow>Compliance · risk</Eyebrow>
        <Title>Buildings at Risk</Title>
        <Explain data-tour-id='atRisk.explain'>
          A Building Passport is flagged <strong>at risk</strong> when its compliance score is{' '}
          <strong>below 75%</strong> <em>or</em> it has <strong>one or more overdue obligations</strong>.
          The compliance score is the share of obligations satisfied, each weighted by its criticality.
          Obligations awaiting evidence are shown for context only. They do not, on their own, place a Building Passport at risk.
          {!loading && (
            <Counted> {buildings.length} of {scored} scored Building Passport{scored === 1 ? '' : 's'} {buildings.length === 1 ? 'is' : 'are'} at risk.</Counted>
          )}
        </Explain>
      </Header>

      {!loading && buildings.length > 0 && (
        <FilterBar>
          <SearchInput placeholder='Search building, location, reason…' value={search} onChange={e => setSearch(e.target.value)} data-tour-id='atRisk.search' />
          <SearchableSelect
            value={band}
            onChange={v => setBand(v)}
            options={[
              { value: 'all', label: 'All at-risk' },
              { value: 'critical', label: 'Critical (below 20%)', color: RAG.critical.fg },
              { value: 'at risk', label: 'At risk (20–49%)', color: RAG.high.fg },
              { value: 'attention', label: 'Attention (50–74%)', color: RAG.medium.fg },
              { value: 'overdue', label: 'Has overdue', color: RAG.critical.fg },
            ]}
            ariaLabel='Filter by risk band'
          />
        </FilterBar>
      )}

      {loading ? (
        <SkeletonList>
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonRow key={i} style={{ animationDelay: `${i * 70}ms` }} />
          ))}
        </SkeletonList>
      ) : buildings.length === 0 ? (
        <Empty>
          <WarningAmberRoundedIcon style={{ fontSize: 34, color: ink.mute }} />
          <p>No buildings at risk. Every scored Building Passport is at or above 75% with no overdue obligations.</p>
        </Empty>
      ) : filtered.length === 0 ? (
        <Empty><p>No buildings match your search or filters.</p></Empty>
      ) : (
        <Card>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th $sortable $active={sort.key === 'name'} onClick={() => sortBy('name')}>Building Passport{sort.key === 'name' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</Th>
                  <Th $right $sortable $active={sort.key === 'score'} onClick={() => sortBy('score')}>Compliance{sort.key === 'score' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</Th>
                  <Th $right $sortable $active={sort.key === 'overdue'} onClick={() => sortBy('overdue')}>Overdue{sort.key === 'overdue' && (sort.dir === 'asc' ? ' ▲' : ' ▼')}</Th>
                  <Th>Why at risk</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {shown.map(b => (
                  <Row key={b.id} onClick={() => navigate(`/bp-list/${b.id}/compliance`)} title={`Open ${b.name}`}>
                    <Td>
                      <BpName>{b.name}</BpName>
                      {(b.city || b.postcode) && (
                        <BpAddr>{[b.city, b.postcode].filter(Boolean).join(' · ')}</BpAddr>
                      )}
                    </Td>
                    <Td $right>
                      <ScorePill
                        $colour={scoreColour(b.score)}
                        title={b.score === null || b.score === undefined
                          ? 'No compliance score yet.'
                          : `Compliance ${formatPercent(b.score)} (${scoreBand(b.score)}). Share of weighted obligations satisfied.`}
                      >
                        {formatPercent(b.score)}
                      </ScorePill>
                    </Td>
                    <Td $right>
                      {b.overdue > 0 ? <Overdue>{b.overdue}</Overdue> : <span>0</span>}
                    </Td>
                    <Td>
                      <Reasons>
                        {(b.reasons || []).map((r, i) => (
                          <ReasonChip key={i} $critical={/critical/i.test(r)}>{r}</ReasonChip>
                        ))}
                      </Reasons>
                    </Td>
                    <Td $right><Arrow>→</Arrow></Td>
                  </Row>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <InfiniteScrollSentinel
            hasMore={visible < filtered.length}
            loading={false}
            onLoadMore={() => setVisible(v => v + PAGE)}
          />
          <Showing>Showing {shown.length} of {filtered.length} at-risk Building Passports{filtered.length !== buildings.length ? ` (filtered from ${buildings.length})` : ''}.</Showing>
        </Card>
      )}
    </Page>
  )
}

export default BuildingsAtRiskPage

const Page = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 24px 48px;
  @media (max-width: ${bpTokens.tablet}) { padding: 18px 16px 40px; }
`

const BackLink = styled(SharedBackLink)`
  margin-bottom: 14px;
`

const Header = styled.div`
  margin-bottom: 20px;
`

const Eyebrow = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${ink.mute};
`

const Title = styled.h1`
  margin: 4px 0 8px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 30px;
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
`

const Explain = styled.p`
  margin: 0;
  font-size: ${font.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.grey[800]};
  max-width: 76ch;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const Counted = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Card = styled.div`
  ${cardStyle}
  overflow: hidden;
`

const TableWrap = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${font.body};
`

const Th = styled.th`
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 12px 16px;
  font-size: ${font.caption};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? ink.base : ink.mute)};
  font-weight: 800;
  border-bottom: 1px solid ${line.base};
  white-space: nowrap;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  &:hover { color: ${({ $sortable }) => ($sortable ? ink.base : ink.mute)}; }
`

const FilterBar = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px;
`
const SearchInput = styled.input`
  flex: 1; min-width: 220px; padding: 8px 12px; font: inherit; font-size: ${font.body};
  border: 1px solid ${line.base}; border-radius: ${radius.pill}; color: ${ink.base}; background: ${surface.card};
  &::placeholder { color: ${ink.mute}; }
  &:focus { outline: none; border-color: ${ink.base}; }
`
const Row = styled.tr`
  cursor: pointer;
  transition: ${transition};
  &:hover { background: ${surface.wash}; }
  &:not(:last-child) td { border-bottom: 1px solid ${line.base}; }
`

const Td = styled.td`
  padding: 12px 16px;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.haiti};
`

const BpName = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const BpAddr = styled.div`
  font-size: ${font.small};
  color: ${ink.mute};
  margin-top: 2px;
`

const ScorePill = styled.span`
  display: inline-flex;
  padding: 2px 10px;
  border-radius: ${radius.pill};
  background: ${({ $colour }) => `${$colour}14`};
  border: 1px solid ${({ $colour }) => `${$colour}40`};
  color: ${({ $colour }) => $colour};
  font-size: ${font.caption};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`

const Overdue = styled.span`
  font-weight: 800;
  color: #b00020;
  font-variant-numeric: tabular-nums;
`

const Reasons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`

const ReasonChip = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  padding: 2px 9px;
  border-radius: ${radius.pill};
  color: ${({ $critical }) => ($critical ? '#b00020' : '#d97706')};
  background: ${({ $critical }) => ($critical ? '#fdecea' : '#fef3c7')};
  border: 1px solid ${({ $critical }) => ($critical ? 'rgba(176,0,32,0.26)' : 'rgba(161,98,7,0.26)')};
`

const Arrow = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 800;
`

const Showing = styled.div`
  padding: 12px 16px;
  font-size: ${font.small};
  color: ${ink.mute};
  text-align: center;
  border-top: 1px solid ${line.base};
`

const Empty = styled.div`
  ${cardStyle}
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  p { margin: 0; max-width: 52ch; line-height: 1.6; color: ${({ theme }) => theme.colors.grey[800]}; }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

const SkeletonList = styled.div`
  ${cardStyle}
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkeletonRow = styled.div`
  height: 52px;
  border-radius: ${radius.sm};
  background: #e7e9ee;
  animation: ${pulse} 1.3s ease-in-out infinite;
`
